import { useState, useCallback, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useFlashDetail } from "@/hooks/useFlashDetail"
import { useArtists } from "@/hooks/useArtists"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useAuthState } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Share2, Send } from "lucide-react"
import { AuthModal } from "@/components/auth/AuthModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { ExclusiveCodePopup } from "@/components/shared/ExclusiveCodePopup"
import { SaveButton } from "@/components/shared/tattoo/SaveButton"
import { SimilarItems } from "@/components/details/SimilarItems"
import { getDiscountInfo, formatDiscountBadge, formatPriceWithDiscount } from "@/lib/discountUtils"
import { useEventById } from "@/hooks/useEventById"
import { CountryFlag } from "@/lib/countryFlags"
import { isNewFlashByCreatedAt } from "@/lib/flashDisplayUtils"

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
}

interface FlashDetailContentProps {
  flashId: string
  onBack?: () => void
  shareUrl?: string
}

interface DetailNavigationState {
  from?: {
    pathname: string
    search?: string
    hash?: string
  }
  scrollPosition?: number
}

export function FlashDetailContent({ flashId, onBack, shareUrl }: FlashDetailContentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { flash, loading, error } = useFlashDetail(flashId)
  const { artists } = useArtists(flash ? [flash.artistId] : [])
  const { trackEvent } = useAnalytics()
  const { user } = useAuthState()
  
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authModalContext, setAuthModalContext] = useState<'save' | 'contact' | 'share'>('contact')
  const [showShareModal, setShowShareModal] = useState(false)
  const [showExclusiveCodePopup, setShowExclusiveCodePopup] = useState(false)
  const [isCodeLoading, setIsCodeLoading] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [compressedImageLoaded, setCompressedImageLoaded] = useState(false)
  const [fullImageLoaded, setFullImageLoaded] = useState(false)

  const imageLoaded = compressedImageLoaded || fullImageLoaded

  const contentRef = useRef<HTMLDivElement>(null)
  const artist = artists[0]

  const [detailPageSavesCount, setDetailPageSavesCount] = useState(0)

  const handleDetailSaveCountChange = useCallback((_isSaved: boolean, newSavesCount: number) => {
    setDetailPageSavesCount(newSavesCount)
  }, [])

  const discountInfo = flash ? getDiscountInfo(flash) : null
  const hasActiveDiscount = discountInfo?.isActive
  const { event: eventData } = useEventById(flash?.eventId)
  const isTime4TatsFlash = !!(flash?.eventId && eventData?.identifier === 'time4tats')

  const showNewFlashTag = useMemo(
    () => isNewFlashByCreatedAt(flash?.createdAt),
    [flash?.createdAt]
  )

  const artistCityCountrySubtitle = useMemo(() => {
    const cityTrimmed = artist?.location?.city?.trim()
    const countryTrimmed = artist?.location?.country?.trim()
    if (cityTrimmed && countryTrimmed) {
      return `${cityTrimmed}, ${countryTrimmed}`
    }
    return cityTrimmed || countryTrimmed || ''
  }, [artist?.location?.city, artist?.location?.country])

  useEffect(() => {
    setDetailPageSavesCount(0)
  }, [flashId])

  // Scroll to top when flashId changes (e.g., when clicking on similar items)
  useEffect(() => {
    if (!flash) return // Wait for content to load
    
    // Use setTimeout to ensure content is rendered and then scroll
    const scrollToTop = () => {
      // Try to find the scrollable parent container
      let scrollableElement: Element | null = null
      
      // Start from our content ref and walk up the tree
      if (contentRef.current) {
        let element = contentRef.current.parentElement
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY
          if (overflowY === 'auto' || overflowY === 'scroll') {
            scrollableElement = element
            break
          }
          element = element.parentElement
        }
      }
      
      // Fallback: try to find dialog with overflow-y-auto
      if (!scrollableElement) {
        scrollableElement = document.querySelector('[role="dialog"]')
      }
      
      // Scroll the container or fallback to window
      if (scrollableElement) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    
    // Delay scroll slightly to ensure new content is rendered
    const timeoutId = setTimeout(scrollToTop, 50)
    return () => clearTimeout(timeoutId)
  }, [flashId, flash])

  useEffect(() => {
    if (flash && artist) {
      trackEvent('item_viewed', {
        item_id: flash.id,
        item_type: 'flash',
        artist_id: flash.artistId,
        source: 'flash_detail'
      })
    }
  }, [flash, artist, trackEvent])

  const handleShowArtist = () => {
    if (artist) {
      navigate(`/artist/${artist.id}`)
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }

    const navigationState = location.state as DetailNavigationState | null
    const from = navigationState?.from

    if (from?.pathname) {
      const backPath = `${from.pathname}${from.search || ''}${from.hash || ''}`
      navigate(backPath, {
        replace: true,
        state: typeof navigationState?.scrollPosition === 'number'
          ? { scrollPosition: navigationState.scrollPosition }
          : undefined
      })
      return
    }

    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/profile')
  }

  const handleContactArtist = useCallback(async () => {
    if (!flash) return
    await trackEvent('artist_message_clicked', {
      artist_id: flash.artistId,
      source: 'flash_detail',
      item_id: flash.id,
      item_type: 'flash'
    })
    navigate(`/chats/new?artistId=${flash.artistId}&flashId=${flash.id}&source=flash_detail`)
  }, [flash, navigate, trackEvent])

  const handleContactArtistClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!user) {
      setAuthModalContext('contact')
      setShowAuthModal(true)
      return
    }

    if (flash?.accessCode) {
      setShowExclusiveCodePopup(true)
      return
    }

    handleContactArtist()
  }, [user, handleContactArtist, flash])

  const handleAuthSuccess = () => {
    if (authModalContext === 'contact') {
      if (flash?.accessCode) {
        setShowExclusiveCodePopup(true)
      } else {
        handleContactArtist()
      }
    } else if (authModalContext === 'share') {
      setShowShareModal(true)
    }
  }

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setShowAuthModal(open)
  }, [])


  const handleShare = () => {
    if (!user) {
      setAuthModalContext('share')
      setShowAuthModal(true)
      return
    }

    const currentUrl = shareUrl || window.location.href

    if (isMobileDevice() && navigator.share) {
      navigator.share({
        title: `Flash Tattoo by ${artist?.displayName || 'Artist'}`,
        text: `Check out this flash tattoo design!`,
        url: currentUrl,
      })
        .then(() => {
          if (!flash) return
          trackEvent('item_shared', {
            item_id: flash.id,
            item_type: 'flash',
            artist_id: flash.artistId,
            source: 'flash_detail',
            share_method: 'native'
          })
        })
        .catch(console.error)
    } else {
      setShowShareModal(true)
    }
  }

  const handleAvatarClick = () => {
    handleShowArtist()
  }

  const handleCodeSubmit = useCallback(async (code: string) => {
    setIsCodeLoading(true)
    setCodeError(null)
    
    try {
      if (code.trim().toLowerCase() === flash?.accessCode?.toLowerCase()) {
        setShowExclusiveCodePopup(false)
        handleContactArtist()
      } else {
        setCodeError(t('exclusiveCode.invalidCode'))
      }
    } catch (error) {
      setCodeError(t('exclusiveCode.invalidCode'))
    } finally {
      setIsCodeLoading(false)
    }
  }, [handleContactArtist, t, flash?.accessCode])

  const handleExclusiveCodePopupOpenChange = useCallback((open: boolean) => {
    setShowExclusiveCodePopup(open)
    if (!open) {
      setCodeError(null)
    }
  }, [])

  if (loading) {
    return (
      <div className="bg-background md:pt-[52px]" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          {/* Back button skeleton */}
          <div className="flex items-center mb-6">
            <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
          </div>

          {/* Image skeleton with 1:1.4 aspect ratio */}
          <div className="mb-12 flex justify-center relative">
            <div className="relative max-w-screen-lg mx-auto rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full">
              <div 
                className="w-full bg-muted animate-pulse"
                style={{ aspectRatio: '1/1.4' }}
              />
            </div>

            {/* Book button skeleton */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
              <div className="h-14 w-48 bg-muted rounded-lg animate-pulse" />
            </div>
          </div>

          {/* Info and Action Row skeleton */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              <div>
                <div className="h-5 w-32 bg-muted rounded animate-pulse mb-2" />
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !flash) {
    return (
      <div className="bg-background md:pt-[52px]">
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">{t('common.error')}</h1>
          </div>
          <div className="text-center py-16">
            <p className="text-muted-foreground">{t('common.flashNotFound')}</p>
            <Button onClick={handleBack} className="mt-4">
              {t('common.goBack')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div ref={contentRef} className="bg-background md:pt-[52px]" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </div>

          {/* Flash Image */}
          <div className="mb-12 flex justify-center relative">
            <div className="relative max-w-screen-lg mx-auto rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
              {!imageLoaded && (
                <div 
                  className="w-full bg-muted animate-pulse"
                  style={{ aspectRatio: flash.width && flash.height ? `${flash.width}/${flash.height}` : '1/1' }}
                />
              )}
              {/* Compressed image loads first for instant preview */}
              {flash.compressedImageUrl && (
                <img
                  src={flash.compressedImageUrl}
                  alt=""
                  aria-hidden="true"
                  className={`w-full h-auto object-cover lg:max-h-[60vh] lg:w-auto transition-opacity duration-300 ${
                    compressedImageLoaded && !fullImageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
                  }`}
                  onLoad={() => setCompressedImageLoaded(true)}
                />
              )}
              {/* Full-resolution image replaces compressed once loaded */}
              <img
                src={flash.imageUrl}
                alt={flash.caption || t('common.flashTattooDesign')}
                className={`w-full h-auto object-cover lg:max-h-[60vh] lg:w-auto transition-opacity duration-300 ${
                  fullImageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
                }`}
                onLoad={() => setFullImageLoaded(true)}
              />
              {flash.accessCode && imageLoaded && (
                <div className="absolute inset-0 bg-red-500/20 rounded-[var(--card-radius)] flex items-center justify-center z-[5]">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-32 h-32 text-red-700/60">
                    <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              {imageLoaded && (
                <div className="absolute inset-0 pointer-events-none z-10 rounded-[var(--card-radius)] overflow-hidden">
                  {showNewFlashTag && !hasActiveDiscount && (
                    <div className="absolute top-2 left-2 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 bg-[var(--save-active)] text-white shadow-sm">
                      <span>NEW</span>
                    </div>
                  )}
                  {hasActiveDiscount && discountInfo && (
                    <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <span>{formatDiscountBadge(discountInfo)}</span>
                    </div>
                  )}
                  {flash.minSize && flash.eventId && !showNewFlashTag && !hasActiveDiscount && (
                    <div className="absolute top-2 left-2 text-xs font-medium rounded-full bg-background text-foreground px-2 py-1">
                      <span>~{flash.minSize}cm</span>
                    </div>
                  )}
                  {!hasActiveDiscount && flash.minPrice && (
                    <div className="absolute top-2 right-2 text-xs font-medium rounded-full bg-background text-foreground px-2 py-1">
                      <span>{t('common.from')} {flash.minPrice}€</span>
                    </div>
                  )}
                  {hasActiveDiscount && discountInfo && (
                    <div className="absolute top-2 right-2 bg-background text-foreground text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                      <span className="text-red-500 font-bold">{formatPriceWithDiscount(discountInfo).currentPrice}</span>
                      <span className="line-through text-muted-foreground">{formatPriceWithDiscount(discountInfo).originalPrice}</span>
                    </div>
                  )}
                  {!flash.eventExclusive && artist?.location?.country && (
                    <div className="absolute bottom-2 left-2">
                      <CountryFlag country={artist.location.country} showBorder={false} customSizeInPx={20} />
                    </div>
                  )}
                  {flash.eventExclusive && (
                    <div
                      className="absolute bottom-2 left-2 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center overflow-hidden"
                      style={{
                        background:
                          'radial-gradient(ellipse 70% 90% at 5% 50%, #5341a5 0%, transparent 70%),' +
                          'radial-gradient(ellipse 65% 85% at 35% 15%, #749ed7 0%, transparent 68%),' +
                          'radial-gradient(ellipse 70% 90% at 65% 85%, #fefe05 0%, transparent 68%),' +
                          'radial-gradient(ellipse 65% 85% at 95% 45%, #fd2d00 0%, transparent 70%),' +
                          '#2a1a4a'
                      }}
                    >
                      <span className="relative z-[1]">Exclusive</span>
                    </div>
                  )}
                  {flash.booked && (
                    <div
                      className="absolute inset-0 z-[12] flex items-center justify-center rounded-[var(--card-radius)] bg-red-600/45"
                      aria-hidden
                    >
                      <span className="text-white font-normal text-lg sm:text-xl tracking-[0.25em] drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
                        BOOKED
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {imageLoaded && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
                <button 
                  onClick={handleContactArtistClick}
                  className={`font-medium px-6 py-4 rounded-lg transition-colors shadow-lg cursor-pointer flex items-center gap-2 whitespace-nowrap border-none ${
                    isTime4TatsFlash
                      ? 'text-white'
                      : 'text-flash-reservation bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                  style={isTime4TatsFlash ? {
                    background:
                      'radial-gradient(ellipse 70% 90% at 5% 50%, #5341a5 0%, transparent 70%),' +
                      'radial-gradient(ellipse 65% 85% at 35% 15%, #749ed7 0%, transparent 68%),' +
                      'radial-gradient(ellipse 70% 90% at 65% 85%, #fefe05 0%, transparent 68%),' +
                      'radial-gradient(ellipse 65% 85% at 95% 45%, #fd2d00 0%, transparent 70%),' +
                      '#2a1a4a'
                  } : undefined}
                  type="button"
                >
                  <Send className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">
                    {t('booking.bookFlash')}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Info and Action Row */}
          <div className="flex items-center justify-between mb-8">
            {imageLoaded ? (
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-75 transition-opacity animate-in fade-in-0 slide-in-from-left-4 duration-300"
                onClick={handleAvatarClick}
              >
                {artist?.avatarUrl && (
                  <img
                    src={artist.avatarUrl}
                    alt={artist.displayName}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <div className="font-medium text-foreground">
                    {artist?.displayName || t('common.artistName')}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {artistCityCountrySubtitle}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
                <div>
                  <div className="h-5 w-32 bg-muted rounded animate-pulse mb-2" />
                  <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                </div>
              </div>
            )}

            {imageLoaded ? (
              <div className="flex items-center gap-3 animate-in fade-in-0 slide-in-from-right-4 duration-300">
                <button 
                  type="button"
                  onClick={handleShare}
                  className="bg-secondary text-secondary-foreground w-12 h-12 rounded-lg flex items-center justify-center transition-colors hover:bg-secondary/80"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                {flash && (
                  <SaveButton
                    item={flash}
                    onSaveStateChange={handleDetailSaveCountChange}
                    className={`bg-secondary text-secondary-foreground hover:bg-secondary/80 h-12 min-h-12 rounded-lg flex items-center justify-center shrink-0 transition-colors py-0 ${
                      detailPageSavesCount > 0
                        ? 'min-w-12 w-auto px-3 gap-1.5'
                        : 'w-12 px-0'
                    }`}
                    iconSize="w-5 h-5"
                    source="flash_detail"
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              </div>
            )}
          </div>

          {/* Similar Items Section */}
          <SimilarItems itemId={flash.id} itemType="flash" fallbackArtistId={flash.artistId} />
        </div>
      </div>
      
      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={handleAuthModalOpenChange}
        onSuccess={handleAuthSuccess}
        source="flash_detail_page"
        defaultMode="signup"
      />
      
      <ShareModal
        isOpen={showShareModal}
        onOpenChange={setShowShareModal}
        url={shareUrl || window.location.href}
        title={`Flash Tattoo by ${artist?.displayName || 'Artist'}`}
        description="Check out this flash tattoo design!"
        imageUrl={flash.imageUrl}
        itemId={flash.id}
        itemType="flash"
        artistId={flash.artistId}
        source="flash_detail"
      />

      <ExclusiveCodePopup
        isOpen={showExclusiveCodePopup}
        onOpenChange={handleExclusiveCodePopupOpenChange}
        onCodeSubmit={handleCodeSubmit}
        isLoading={isCodeLoading}
        error={codeError || undefined}
      />

    </>
  )
}
